/* Copyright (c) Trimble Inc. All rights reserved. */

import { getUserTable } from './Auth.db';
import { User, UserProfile } from '../models/User.model';
import { Logger } from '@tcp-base/toolkit';

// Helper functions for generating display name and initials
function generateDisplayName(user: User): string {
  if (user.given_name && user.family_name) {
    return `${user.given_name} ${user.family_name}`;
  } else if (user.given_name) {
    return user.given_name;
  } else if (user.family_name) {
    return user.family_name;
  } else if (user.email) {
    return user.email.split('@')[0];
  }
  return '';
}

function generateInitials(user: User): string {
  if (user.given_name && user.family_name) {
    return `${user.given_name.charAt(0)}${user.family_name.charAt(0)}`.toUpperCase();
  } else if (user.given_name) {
    return user.given_name.charAt(0).toUpperCase();
  } else if (user.family_name) {
    return user.family_name.charAt(0).toUpperCase();
  } else if (user.email) {
    return user.email.charAt(0).toUpperCase();
  }
  return '';
}

class UserTableImpl {
  /**
   * Save user data to IndexedDB
   */
  async saveUserData(userData: User): Promise<void> {
    try {
      const userTable = await getUserTable();

      // Store each field directly with just the value (key -> value, not key -> {key, value})
      if (userData.given_name !== undefined) {
        await userTable.put('given_name', userData.given_name);
      }
      if (userData.family_name !== undefined) {
        await userTable.put('family_name', userData.family_name);
      }
      if (userData.email !== undefined) {
        await userTable.put('email', userData.email);
      }
      if (userData.picture !== undefined && userData.picture !== null) {
        await userTable.put('picture', userData.picture);
      }
      if (userData.account_id !== undefined) {
        await userTable.put('account_id', userData.account_id);
      }
      if (userData.user_id !== undefined) {
        await userTable.put('user_id', userData.user_id);
      }
    } catch (error) {
      Logger.error('Failed to save user data to IndexedDB:', error);
      throw error;
    }
  }

  /**
   * Get user data from IndexedDB
   */
  async getUserData(): Promise<UserProfile> {
    try {
      const userTable = await getUserTable();

      // Get individual values directly by key
      const userData: User = {};

      const givenName = await userTable.get('given_name');
      if (givenName !== undefined) {
        userData.given_name = givenName;
      }

      const familyName = await userTable.get('family_name');
      if (familyName !== undefined) {
        userData.family_name = familyName;
      }

      const email = await userTable.get('email');
      if (email !== undefined) {
        userData.email = email;
      }

      const picture = await userTable.get('picture');
      if (picture !== undefined) {
        userData.picture = picture;
      }
      const accountId = await userTable.get('account_id');
      if (accountId !== undefined) {
        userData.account_id = accountId;
      }

      const userId = await userTable.get('user_id');
      if (userId !== undefined) {
        userData.user_id = userId;
      }

      // Generate display name and initials
      const userProfile: UserProfile = {
        ...userData,
        displayName: generateDisplayName(userData),
        initials: generateInitials(userData),
      };

      return userProfile;
    } catch (error) {
      Logger.error('Failed to get user data from IndexedDB:', error);
      throw error;
    }
  }

  /**
   * Remove all user data from IndexedDB
   */
  async removeUserData(): Promise<void> {
    try {
      const userTable = await getUserTable();

      // Clear all user data from the user table
      await userTable.clear();
    } catch (error) {
      Logger.error('Failed to remove user data from IndexedDB:', error);
      throw error;
    }
  }
}

// Create singleton instance
export const UserTable = new UserTableImpl();
